/*
 * Demo mode: the real app on the in-browser mock API, signed in to a seeded workspace.
 *
 *   /demo, /demo/ads, …   in any build: the router runs under /demo, so links stay in the demo
 *   VITE_DEMO=1 builds    (bun run build:demo) the whole site is the demo
 *
 * The decision is made once per page load, from the path. A demo page installs the mock
 * (its own lazy chunk) before the app renders; the real app at / never loads it, and the
 * demo never reaches /api, the session cookie or the CSRF token.
 *
 * Nothing leaves the browser: the mock answers every API call, other origins can't be
 * fetched, and links that would leave the app (Telegram, TON wallets, checkout, other
 * sites) open a "Not available in the demo" sheet. Same-site links outside /demo are
 * pulled back into it.
 */
const BASE = import.meta.env.BASE_URL.replace(/\/?$/, "/");
const ALWAYS = import.meta.env.VITE_DEMO === "1";
/** Where the demo lives: "/demo" (or the build's base in VITE_DEMO builds). No trailing slash. */
export const DEMO_BASE = ALWAYS ? BASE.slice(0, -1) : BASE + "demo";
export const DEMO_UNAVAILABLE = "beacon:demo-unavailable";

let on = false;
export const isDemo = () => on;

const inDemo = (path: string) => path === DEMO_BASE || path.startsWith(DEMO_BASE + "/");

/** Decide whether this page load is the demo. */
export function detectDemo(): boolean {
  on = ALWAYS || inDemo(location.pathname);
  return on;
}

/** The router's basename: /demo in the demo, the build's base otherwise. */
export function routerBasename(): string {
  return on ? DEMO_BASE : BASE.slice(0, -1);
}

/** An absolute URL to a screen inside the demo, e.g. demoUrl("/share/x"). */
export const demoUrl = (path: string) => location.origin + DEMO_BASE + path;

/** Keep a page out of search results (the demo and the gallery). */
export function noindex() {
  if (document.querySelector('meta[name="robots"]')) return;
  const meta = document.createElement("meta");
  meta.name = "robots";
  meta.content = "noindex, nofollow";
  document.head.appendChild(meta);
}

/** Install the mock and the guards that keep the demo free of side effects. */
export async function startDemo() {
  noindex();
  // Backstop for what the guards can't see (wallet icons, embeds): only this site's own resources.
  const csp = document.createElement("meta");
  csp.httpEquiv = "Content-Security-Policy";
  csp.content = "connect-src 'self'; img-src 'self' data: blob:; media-src 'self' data: blob:; frame-src 'self'";
  document.head.appendChild(csp);
  const { installMock } = await import("../api/mock");
  installMock({ demo: true, linkBase: DEMO_BASE });
  installGuards();
}

/** Reset: throw away every change and reload from the seed. */
export function resetDemo() {
  location.reload();
}

export function leaveDemo() {
  location.href = BASE;
}

function unavailable(what: string) {
  window.dispatchEvent(new CustomEvent(DEMO_UNAVAILABLE, { detail: { what } }));
}

type Target = { what: string } | { path: string } | null;

/** What following this URL would do: leave the app (blocked), leave /demo (pulled back), or nothing. */
function classify(href: string): Target {
  let u: URL;
  try {
    u = new URL(href, location.href);
  } catch {
    return null;
  }
  if (u.protocol === "ton:" || u.protocol === "tg:" || u.protocol === "tonkeeper:") return { what: "Opening a wallet" };
  if (u.protocol === "mailto:" || u.protocol === "tel:") return { what: "Opening that link" };
  if (u.origin === location.origin) {
    if (inDemo(u.pathname) || /\.[a-z0-9]+$/i.test(u.pathname)) return null;
    return { path: DEMO_BASE + u.pathname + u.search + u.hash };
  }
  if (/(^|\.)t\.me$|(^|\.)telegram\.(org|me)$/.test(u.hostname)) return { what: u.pathname.startsWith("/$") || u.pathname.includes("invoice") ? "Checkout" : "Opening Telegram" };
  return { what: "Opening other sites" };
}

/** Client-side navigation to a demo path; the router follows popstate. */
function go(path: string) {
  history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function installGuards() {
  // Other origins: the demo makes no network calls of its own (wallet lists, widgets, …).
  const fetchNext = window.fetch;
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (new URL(raw, location.href).origin !== location.origin) return Promise.reject(new TypeError("Network access is off in the demo"));
    return fetchNext(input, init);
  };
  // Links: capture phase, so screens' own handlers never see the click.
  document.addEventListener(
    "click",
    (e) => {
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || e.defaultPrevented) return;
      const t = classify(a.getAttribute("href")!);
      if (!t) return;
      e.preventDefault();
      e.stopPropagation();
      if ("what" in t) unavailable(t.what);
      else if (a.target === "_blank") window.open(t.path, "_blank", "noopener");
      else go(t.path);
    },
    true,
  );
  const open = window.open.bind(window);
  window.open = (url?: string | URL, target?: string, features?: string) => {
    const t = url ? classify(String(url)) : null;
    if (t && "what" in t) {
      unavailable(t.what);
      return null;
    }
    return open(t ? t.path : url, target, features);
  };
  // Inside Telegram: the client opens links, invoices and chats itself.
  const tg = (window as { Telegram?: { WebApp?: Record<string, unknown> } }).Telegram?.WebApp;
  if (tg) {
    // Links are classified like anchors; invoices and inline queries are always blocked.
    const guard = (name: string, what: string, link = false) => {
      if (typeof tg[name] !== "function") return;
      const real = (tg[name] as (...a: unknown[]) => unknown).bind(tg);
      tg[name] = (url: unknown, ...rest: unknown[]) => {
        const t = link && typeof url === "string" ? classify(url) : { what };
        if (!t) return real(url, ...rest);
        if ("path" in t) return go(t.path);
        unavailable(t.what);
      };
    };
    guard("openLink", "Opening other sites", true);
    guard("openTelegramLink", "Opening Telegram", true);
    guard("openInvoice", "Checkout");
    guard("switchInlineQuery", "Sharing to a chat");
  }
}
